import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Select } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { IconFeatured } from '@bynder/icons';
import ComponentCollectionService, { ComponentCollection } from 'packages/services/ComponentCollectionService';
import useDebouncedState from 'packages/hooks/useDebouncedState';

const COLLECTIONS_PAGE_SIZE = 50;

interface Props {
    selectedCollection: string | null;
    onChange: (collection: string | null) => void;
    isOpen: boolean;
    ariaLabelledBy: string;
}

export const CollectionSelector = ({ selectedCollection, onChange, isOpen, ariaLabelledBy }: Props) => {
    const { translate } = useTranslate();
    const {
        state: collectionSearch,
        debouncedState: debouncedCollectionSearch,
        setState: setCollectionSearch,
    } = useDebouncedState('', 500);
    const [collections, setCollections] = useState<ComponentCollection[]>([]);
    const [collectionsPage, setCollectionsPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [isCollectionsLoading, setIsCollectionsLoading] = useState(false);
    const [selectedCollectionName, setSelectedCollectionName] = useState<string | null>(null);

    const observer = useRef<IntersectionObserver>();
    const lastCollectionElementRef = useCallback(
        (node: HTMLDivElement) => {
            if (isCollectionsLoading) {
                return;
            }

            if (observer.current) {
                observer.current.disconnect();
            }

            observer.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasMore) {
                    setCollectionsPage((prevPage) => prevPage + 1);
                }
            });

            if (node) {
                observer.current.observe(node);
            }
        },
        [isCollectionsLoading, hasMore],
    );

    useEffect(() => {
        if (isOpen) {
            setCollectionSearch('');
            setCollectionsPage(0);
            setCollections([]);
            setHasMore(true);
            setSelectedCollectionName(null);
        }
    }, [isOpen]);

    useEffect(() => {
        let isMounted = true;

        if (isOpen) {
            setIsCollectionsLoading(true);
            ComponentCollectionService.getComponentCollections({
                size: COLLECTIONS_PAGE_SIZE,
                page: collectionsPage,
                collectionSearch: debouncedCollectionSearch,
            })
                .then((response) => {
                    if (isMounted) {
                        if (response.status !== 200) {
                            return;
                        }

                        const newCollections = response.json.content;
                        setCollections((prev) =>
                            collectionsPage === 0 ? newCollections : [...prev, ...newCollections],
                        );
                        setHasMore(!response.json.last);
                    }
                })
                .finally(() => {
                    if (isMounted) {
                        setIsCollectionsLoading(false);
                    }
                });
        }

        return () => {
            isMounted = false;
        };
    }, [isOpen, debouncedCollectionSearch, collectionsPage]);

    const handleOnChange = (id: string | null) => {
        const name = collections.find((c) => c.id.toString() === id)?.name || null;
        setSelectedCollectionName(name);
        onChange(id);
    };

    return (
        <Select
            value={selectedCollection}
            onChange={handleOnChange}
            label={selectedCollectionName || translate('components.create.modal.collection.selector.placeholder')}
            aria-labelledby={ariaLabelledBy}
        >
            <Select.SearchInput
                value={collectionSearch}
                onChange={(value) => {
                    setCollectionSearch(value);
                    setCollectionsPage(0);
                }}
                placeholder={translate('components.create.modal.collection.selector.search.placeholder')}
                aria-label={translate('components.create.modal.collection.selector.search.ariaLabel')}
            />

            {collections.length === 0 ? (
                <Select.Text>{translate('components.create.modal.collection.selector.search.noResults')}</Select.Text>
            ) : null}

            {collections.length > 0 ? (
                <>
                    {collections.map((option) => (
                        <Select.Item key={option.id} value={option.id.toString()} icon={<IconFeatured />}>
                            {option.name}
                        </Select.Item>
                    ))}
                    <div ref={lastCollectionElementRef} style={{ height: 1, width: '100%' }} />
                </>
            ) : null}
        </Select>
    );
};
